import { useMemo, useState } from "react";

import useCloudPermissions from "../../hooks/useCloudPermissions";
import { callFunctionIfExists } from "../../utils/GeneralUtils";

interface TableHeaderProps {
  item: any;
  orderBy: { Field: string; Direction: string } | undefined;
  headerMapRef: React.MutableRefObject<Record<string, string> | undefined>;
  onSort: (val: any) => void;
  onColumnRemove?: (val: any) => void;
  multiSubitemFieldAPIName?: string;
  isCustomTable?: boolean;
}

/**
 * TableHeader - Table header component built specifically for AppTable.
 * @param {object} item - The table header item
 * @param {object} orderBy - The query OrderBy object.
 * @param {} headerMap - Map for header display values. headerID -> displayValue
 * @param {} showOptions - Whether or not to show (...) options menu for header.
 */
const TableHeader = ({
  item,
  orderBy,
  headerMapRef,
  onSort,
  onColumnRemove,
  multiSubitemFieldAPIName = "",
  isCustomTable = false,
}: TableHeaderProps) => {
  const { cPermissions } = useCloudPermissions();

  const [isHovered, setIsHovered] = useState(false);

  const headerMap = headerMapRef.current;
  const headerID = item?.column?.id;
  const isLookUp = headerID.includes(".");

  let displayValue = isLookUp
    ? `${headerID.split(".")[0]} - ${headerID.split(".")[1]}`
    : headerMap?.[headerID];
  const orderSubitem = useMemo(
    () => orderBy?.Field?.split?.(".")?.[1],
    [orderBy?.Field]
  );

  const orderField = orderSubitem ?? orderBy?.Field;
  const order =
    orderField === headerID || multiSubitemFieldAPIName === headerID
      ? orderBy?.Direction
      : null;

  const updateOrderBy = () => {
    const newOrder = !order ? "ASC" : order === "ASC" ? "DESC" : null;

    let updatedOrderBy = newOrder
      ? {
          Field: headerID,
          Direction: newOrder.toUpperCase(),
        }
      : null;

    callFunctionIfExists(onSort, updatedOrderBy);
  };

  if (!displayValue) {
    displayValue = "(Deleted Field)";
  }

  return (
    <div
      className="d-flex align-items-center justify-content-between no-select"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="d-flex align-items-center justify-content-between gap-2 w-100"
        onClick={() => {
          if (isCustomTable) return updateOrderBy();

          if (!onColumnRemove || !cPermissions.cloud_modify_search) return;
          updateOrderBy();
        }}
      >
        <span style={{ minWidth: 80 }}>{displayValue}</span>
        <div className="d-flex cursor-pointer">
          {isHovered || order ? (
            <div className="d-flex flex-column">
              <i
                className={`mdi mdi-menu-up text-${
                  order === "ASC" ? "primary" : "light"
                }`}
                style={{
                  fontSize: 33,
                  lineHeight: 0.3,
                }}
              />
              <i
                className={`mdi mdi-menu-down text-${
                  order === "DESC" ? "primary" : "light"
                }`}
                style={{
                  fontSize: 33,
                  lineHeight: 0.3,
                }}
              />
            </div>
          ) : (
            <div style={{ width: 33 }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default TableHeader;
